import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';

import {
  type AdminApproval,
  AdminApprovalType,
  type Invite,
  inviteDeleteInvite,
  inviteListInvitesOptions,
  inviteListInvitesQueryKey,
  inviteResendInviteEmail,
} from '@column/api';
import { Button, Dropdown, DropdownOption, Icon, Tooltip } from '@column/column-ui-kit';
import {
  DataTable,
  type DataTableColumnDef,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@column/ui';

import { PageActions } from '~/app/Layout/PageFrameContext';
import { Loading, SectionHeader } from '~/components';
import { useDialog } from '~/components/Dialog/Provider';
import {
  StatusBadge,
  Empty as TableEmpty,
  Timestamp,
  getTimestampColumn,
  tableDefaults,
  useTimeFormat,
} from '~/components/Table';
import {
  AdminApprovalReviewDialog,
  PendingApprovalStatus,
  ReviewChangesButton,
  invalidateAdminApprovals,
  rolesQueryOptions,
  teamQueryKeys,
  useAdminApprovalReview,
  usePendingAdminApprovals,
  usersQueryOptions,
} from '~/lib/approvals';
import { DashboardUserRedacted, FactorTypeResponse, UserRepository } from '~/repositories';
import { OwnerRoleId, getRoleLabel, precannedRolesByID, precannedRolesByName } from '~/repositories/RoleRepository';
import { Factor } from '~/repositories/UserRepository';
import { useHelpSidebarStore } from '~/stores/HelpSidebar';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { Flex, Inner } from '~/styles';

import { InviteUsersModal } from './_components/InviteUsersModal';

interface UserListInfo {
  id: string;
  name: string;
  isOwner: boolean;
}

interface UserListRole {
  id: string;
  name: string;
}

const RoleHelpButton = styled(Button)`
  padding: 0;
`;

const INVITE_REQUIRES_MFA_TOOLTIP = 'Enable two-factor authentication in your profile to invite users.';
const MANAGE_USERS_PERMISSION_TOOLTIP = 'You do not have permission to manage users.';

interface UserRow {
  id: string;
  info: UserListInfo;
  email: string;
  factors: Factor[];
  role: UserListRole;
  user: DashboardUserRedacted;
}

interface InviteRow {
  id: string;
  email: string;
  platformRole: string;
  expiredAt?: Date;
  created?: Date;
  createdBy?: string;
  invite?: Invite;
  approval?: AdminApproval;
}

const columnWidths = {
  primary: 'minmax(300px, 1fr)',
  secondary: 'minmax(240px, 1fr)',
  tertiary: 'minmax(160px, 1fr)',
  last: 'minmax(260px, 300px)',
};

export const PageTeam: React.FC = () => {
  const { currentUser, currentPlatform, isAuthorized } = useSessionStore();
  const canWriteUsers = isAuthorized({ permission: 'users', permissionLevel: 'write' });
  const { addSuccessNotification, addDangerNotification } = useNotificationStore();
  const { openAlert } = useDialog();
  const openHelpSidebar = useHelpSidebarStore((state) => state.openHelpSidebar);

  const queryClient = useQueryClient();
  const [isInviteModalOpen, setIsInviteModalOpen] = useState<boolean>(false);
  const pendingInvites = usePendingAdminApprovals(AdminApprovalType.USER_INVITE);
  const pendingRoleAssigns = usePendingAdminApprovals(AdminApprovalType.USER_ROLE_ASSIGN);
  const pendingRoleAssignByUser = useMemo(
    () => new Map(pendingRoleAssigns.map((a) => [a.request.user_role_assign?.dashboard_user_id, a])),
    [pendingRoleAssigns]
  );
  const visibleApprovals = useMemo(
    () => [...pendingRoleAssigns, ...pendingInvites],
    [pendingInvites, pendingRoleAssigns]
  );
  const { openReview, getApprovalActions, startReview, dialogProps } = useAdminApprovalReview(visibleApprovals);
  const openInviteModal = useCallback(() => setIsInviteModalOpen(true), []);
  const closeInviteModal = useCallback(() => setIsInviteModalOpen(false), []);

  const hasMfaEnabled = useMemo(
    () => currentUser?.factors?.some((factor) => factor.factorVerified) ?? false,
    [currentUser]
  );

  const {
    data: usersResponse,
    isLoading: isUsersLoading,
    isFetching: isUsersFetching,
  } = useQuery({
    ...usersQueryOptions(currentPlatform?.id),
  });

  const {
    data: invitesResponse,
    isLoading: isInvitesLoading,
    isFetching: isInvitesFetching,
  } = useQuery({
    ...inviteListInvitesOptions(),
    enabled: !!currentPlatform,
  });

  const invites = invitesResponse?.invites ?? [];

  const { data: rolesResponse } = useQuery(rolesQueryOptions());

  const rolesList: DropdownOption[] = useMemo(() => {
    return (rolesResponse?.roles || []).map((role) => ({
      label: getRoleLabel(role.name),
      value: role.id,
    }));
  }, [rolesResponse]);

  const deleteInviteMutation = useMutation({
    mutationFn: async (email: string) => {
      if (!currentPlatform) throw new Error('No platform selected');
      return inviteDeleteInvite({ path: { platform_id: currentPlatform.id, email }, throwOnError: true });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: inviteListInvitesQueryKey() });
      addSuccessNotification({
        content: 'Invite deleted',
      });
    },
    onError: (error: any) => {
      addDangerNotification({
        content: error.message || 'Failed to delete invite',
      });
    },
  });

  const resendInviteMutation = useMutation({
    mutationFn: async (email: string) => {
      if (!currentPlatform) throw new Error('No platform selected');
      return inviteResendInviteEmail({ body: { email }, throwOnError: true });
    },
    onSuccess: () => {
      addSuccessNotification({
        content: 'Invite resent',
      });
    },
    onError: (error: any) => {
      addDangerNotification({
        content: error.message || 'Failed to resend invite',
      });
    },
  });

  const updateRoleMutation = useMutation({
    mutationFn: async ({ userId, roleId }: { userId: string; roleId: string }) => {
      if (!currentPlatform) throw new Error('No platform selected');
      return UserRepository.updatePlatformRole(currentPlatform.id, {
        userId,
        customRoleId: precannedRolesByID[roleId] ? undefined : roleId,
        precannedRoleName: precannedRolesByID[roleId] ? precannedRolesByID[roleId].name : undefined,
      });
    },
    onSuccess: (response, variables) => {
      queryClient.invalidateQueries({ queryKey: teamQueryKeys.users(currentPlatform?.id) });
      invalidateAdminApprovals(queryClient);
      const role = rolesResponse?.roles?.find((r) => r.id === variables.roleId);
      if (response.pendingApproval) {
        addSuccessNotification({
          content: `Role change to "${role ? getRoleLabel(role.name) : 'new role'}" submitted for approval.`,
        });
      } else if (role) {
        addSuccessNotification({
          content: `Role updated to "${getRoleLabel(role.name)}" successfully.`,
        });
      }
    },
    onError: (error: any) => {
      addDangerNotification({
        content: error.message || 'Failed to update role',
      });
    },
  });

  const removeUserMutation = useMutation({
    mutationFn: async ({ user, platformId }: { user: DashboardUserRedacted; platformId: string }) => {
      return UserRepository.removeUserFromPlatform({
        dashboardUserId: user.id,
        platformId,
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: teamQueryKeys.users(currentPlatform?.id) });
      addSuccessNotification({
        content: `"${variables.user.firstName} ${variables.user.lastName}" successfully removed from this platform.`,
      });
    },
    onError: (error: any) => {
      addDangerNotification({
        content: error.message || 'Failed to remove user',
      });
    },
  });

  const deleteFactorMutation = useMutation({
    mutationFn: async ({ factorId }: { factorId: string }) => {
      return UserRepository.deleteFactor({ factorId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teamQueryKeys.users(currentPlatform?.id) });
      addSuccessNotification({
        content: 'Authenticator app disabled',
      });
    },
    onError: (error: any) => {
      addDangerNotification({
        content: error.message || 'Failed to disable authenticator',
      });
    },
  });

  const handleRoleChange = useCallback(
    (userId: string, email: string, roleId: string) => {
      if (!currentPlatform || !rolesResponse) return;
      updateRoleMutation.mutate({ userId, roleId });
    },
    [currentPlatform, rolesResponse, updateRoleMutation]
  );

  const handleRemoveUser = useCallback(
    (user: DashboardUserRedacted, platformId: string) => {
      removeUserMutation.mutate({ user, platformId });
    },
    [removeUserMutation]
  );

  const handleUserRemoveConfirm = useCallback(
    (user: DashboardUserRedacted, platformId: string) => {
      openAlert({
        headline: `Remove ${user.firstName}?`,
        content: `Are you sure you want to remove ${user.firstName} ${user.lastName} from this platform?`,
        confirmButton: {
          label: 'Remove',
          variant: 'danger',
        },
        onSuccess: () => handleRemoveUser(user, platformId),
      });
    },
    [handleRemoveUser, openAlert]
  );

  const handleUserDisableTotpMfa = useCallback(
    (user: DashboardUserRedacted, totpFactor: Factor) => {
      openAlert({
        headline: `Disable Authenticator App for ${user.firstName}`,
        content: `This action cannot be undone. If authenticator app two-factor authentication is disabled, it can
          only be re-enabled by ${user.firstName}.`,
        confirmButton: {
          label: 'Disable',
          variant: 'danger',
        },
        onSuccess: () => {
          deleteFactorMutation.mutate({ factorId: totpFactor.factorId });
        },
      });
    },
    [deleteFactorMutation, openAlert]
  );

  const deleteInvite = useCallback(
    (email: string) => {
      deleteInviteMutation.mutate(email);
    },
    [deleteInviteMutation]
  );

  const resendInvite = useCallback(
    (email: string) => {
      resendInviteMutation.mutate(email);
    },
    [resendInviteMutation]
  );

  const userList: UserRow[] = useMemo(() => {
    if (!currentUser || !usersResponse) return [];
    return usersResponse.dashboardUsers.map((user) => ({
      id: user.id,
      info: {
        id: user.id,
        name: `${user.firstName} ${user.lastName}`,
        isOwner: user.platformRole.name === 'owner',
      },
      email: user.email,
      factors: user.factors,
      role: {
        id: user.platformRole.id,
        name: user.platformRole.name,
      },
      user,
    }));
  }, [currentUser, usersResponse]);

  const userColumns: DataTableColumnDef<UserRow>[] = useMemo(
    () =>
      currentPlatform
        ? [
            {
              header: 'Name',
              accessor: 'info',
              width: columnWidths.primary,
              cell: ({ value }) => (
                <DataTable.TextCell value={value.name} weight="medium">
                  <Flex gap={4}>
                    {(value.id === currentUser?.id || value.isOwner) && (
                      <>
                        {value.id === currentUser?.id && <StatusBadge tone="info">You</StatusBadge>}
                        {value.isOwner && <StatusBadge>Owner</StatusBadge>}
                      </>
                    )}
                  </Flex>
                </DataTable.TextCell>
              ),
            },
            {
              header: 'Email',
              accessor: 'email',
              width: columnWidths.secondary,
              cell: ({ value }) => <DataTable.TextCell value={value} />,
            },
            {
              header: '2FA',
              accessor: 'factors',
              width: columnWidths.tertiary,
              cell: ({ value }) => {
                const factors: Factor[] = value ?? [];
                if (factors.length === 0) {
                  return <StatusBadge>Not enabled</StatusBadge>;
                }
                if (
                  factors.some((factor) => factor.factorVerified && factor.factorType === FactorTypeResponse.WEBAUTHN)
                ) {
                  return <StatusBadge tone="success">Passkey</StatusBadge>;
                }
                if (factors.some((factor) => factor.factorVerified && factor.factorType === FactorTypeResponse.TOTP)) {
                  return <StatusBadge tone="success">Authenticator</StatusBadge>;
                }
                if (factors.some((factor) => factor.factorVerified && factor.factorType === FactorTypeResponse.SMS)) {
                  return <StatusBadge tone="success">SMS</StatusBadge>;
                }
                return null;
              },
            },
            {
              header: (
                <RoleHelpButton
                  icon={<Icon.CircleQuestionmark />}
                  onClick={() => openHelpSidebar('Roles')}
                  variant="subtle"
                  size="small"
                >
                  Role
                </RoleHelpButton>
              ),
              accessor: 'role',
              width: columnWidths.last,
              cell: ({ row, value }) => {
                const activeRoleId = value.id === OwnerRoleId ? precannedRolesByName.admin.id : value.id;
                const isDisabled =
                  !canWriteUsers ||
                  currentUser?.id === value.id ||
                  (value.id === OwnerRoleId && currentPlatform.id === currentUser?.defaultPlatformId);
                const pendingAssign = pendingRoleAssignByUser.get(row.id);
                if (pendingAssign) {
                  return (
                    <DataTable.Cell className="px-0">
                      <PendingApprovalStatus />
                    </DataTable.Cell>
                  );
                }
                return (
                  <Tooltip
                    content={MANAGE_USERS_PERMISSION_TOOLTIP}
                    isOpen={canWriteUsers ? false : undefined}
                    placement="left"
                    className="w-full !max-w-none"
                  >
                    <Select
                      value={activeRoleId}
                      onValueChange={(roleId) => handleRoleChange(row.id, row.email, roleId)}
                      disabled={isDisabled}
                    >
                      <SelectTrigger size="sm" variant="outline" className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {rolesList.map((role) => (
                          <SelectItem key={role.value} value={role.value}>
                            {role.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Tooltip>
                );
              },
            },
          ]
        : [],
    [currentPlatform, currentUser, canWriteUsers, rolesList, handleRoleChange, openHelpSidebar, pendingRoleAssignByUser]
  );

  const userRowAction = useCallback(
    (row: UserRow) => {
      const smsFactor = row.user.factors.find(
        (factor) => factor.factorVerified && factor.factorType === FactorTypeResponse.SMS
      );
      const totpFactor = row.user.factors.find(
        (factor) => factor.factorVerified && factor.factorType === FactorTypeResponse.TOTP
      );

      const pendingAssign = pendingRoleAssignByUser.get(row.id);

      return (
        <Dropdown
          isInside
          positionRight
          options={[
            ...(pendingAssign ? getApprovalActions(pendingAssign) : []),
            {
              label: 'Reset Authenticator 2FA',
              isDisabled: !canWriteUsers || !smsFactor || !totpFactor,
              tooltip: !canWriteUsers
                ? { content: MANAGE_USERS_PERMISSION_TOOLTIP, placement: 'left' }
                : !totpFactor
                  ? { content: 'User does not have Authenticator 2FA configured', placement: 'left' }
                  : !smsFactor
                    ? { content: 'Authenticator 2FA may not be reset without SMS 2FA enabled', placement: 'left' }
                    : undefined,
              onClick: totpFactor ? () => handleUserDisableTotpMfa(row.user, totpFactor) : undefined,
            },
            {
              label: 'Remove',
              isDanger: true,
              isDisabled: !canWriteUsers || currentUser?.id === row.user.id || row.user.platformRole.name === 'owner',
              tooltip: !canWriteUsers ? { content: MANAGE_USERS_PERMISSION_TOOLTIP, placement: 'left' } : undefined,
              onClick: () => handleUserRemoveConfirm(row.user, row.user.platformRole.platformId),
            },
          ]}
          variant="dots"
        />
      );
    },
    [
      getApprovalActions,
      canWriteUsers,
      currentUser,
      handleUserDisableTotpMfa,
      handleUserRemoveConfirm,
      pendingRoleAssignByUser,
    ]
  );

  const inviteList: InviteRow[] = useMemo(
    () => [
      ...pendingInvites.map((approval) => {
        const request = approval.request.user_invite;
        return {
          id: approval.id,
          email: request?.email ?? '',
          platformRole:
            request?.platform_role ?? rolesResponse?.roles?.find((role) => role.id === request?.role_id)?.name ?? '',
          created: new Date(approval.created_at),
          createdBy: approval.requested_by.name,
          approval,
        };
      }),
      ...invites.map((invite) => {
        const created = invite.created_at ? new Date(invite.created_at) : undefined;

        return {
          id: invite.platform_id ?? '',
          email: invite.email,
          platformRole: invite.platform_role,
          expiredAt: invite.expired_at ? new Date(invite.expired_at) : undefined,
          created,
          createdBy: invite.created_by?.name,
          invite,
        };
      }),
    ],
    [invites, pendingInvites, rolesResponse]
  );

  const { timeFormat, onTimeFormatChange } = useTimeFormat('team-invites');

  const inviteRowAction = useCallback(
    (row: InviteRow) => {
      const { approval } = row;
      if (approval) {
        const options = getApprovalActions(approval);
        return options.length ? <Dropdown isInside positionRight options={options} variant="dots" /> : null;
      }
      return (
        <Dropdown
          isInside
          positionRight
          options={[
            {
              label: 'Resend',
              isDisabled: !canWriteUsers,
              tooltip: !canWriteUsers ? { content: MANAGE_USERS_PERMISSION_TOOLTIP, placement: 'left' } : undefined,
              onClick: () => resendInvite(row.email),
            },
            {
              label: 'Delete',
              isDisabled: !canWriteUsers,
              tooltip: !canWriteUsers ? { content: MANAGE_USERS_PERMISSION_TOOLTIP, placement: 'left' } : undefined,
              onClick: () => deleteInvite(row.email),
              isDanger: true,
            },
          ]}
          variant="dots"
        />
      );
    },
    [getApprovalActions, canWriteUsers, deleteInvite, resendInvite]
  );

  const inviteColumns: DataTableColumnDef<InviteRow>[] = useMemo(
    () => [
      {
        header: 'Email',
        accessor: 'email',
        width: columnWidths.primary,
        cell: ({ value }) => <DataTable.TextCell value={value} weight="medium" />,
      },
      {
        header: 'Status',
        accessor: 'expiredAt',
        width: columnWidths.secondary,
        cell: ({ row, value }) => (
          <DataTable.Cell>
            {row.approval ? (
              <PendingApprovalStatus />
            ) : value && value < new Date() ? (
              <StatusBadge tone="danger">Expired</StatusBadge>
            ) : (
              <StatusBadge tone="success">Invite sent</StatusBadge>
            )}
          </DataTable.Cell>
        ),
      },
      {
        header: (
          <RoleHelpButton
            icon={<Icon.CircleQuestionmark />}
            onClick={() => openHelpSidebar('Roles')}
            variant="subtle"
            size="small"
          >
            Role
          </RoleHelpButton>
        ),
        accessor: 'platformRole',
        width: columnWidths.tertiary,
        cell: ({ value }) => <StatusBadge>{getRoleLabel(value)}</StatusBadge>,
      },
      {
        ...getTimestampColumn<InviteRow>({ accessor: 'created', timeFormat, onTimeFormatChange }),
        width: columnWidths.last,
        cell: ({ row, value }) => (
          <DataTable.Cell>
            <span className="flex items-center gap-1 whitespace-nowrap">
              {row.createdBy && (
                <>
                  <span className="text-xs text-muted-foreground">{row.createdBy}</span>
                  <span aria-hidden className="text-xs text-muted-foreground select-none">
                    ·
                  </span>
                </>
              )}
              <Timestamp date={value} timeFormat={timeFormat} />
            </span>
          </DataTable.Cell>
        ),
      },
    ],
    [onTimeFormatChange, openHelpSidebar, timeFormat, usersResponse]
  );

  return (
    <>
      <AdminApprovalReviewDialog {...dialogProps} />
      <InviteUsersModal
        onClose={closeInviteModal}
        onSave={() => {
          closeInviteModal();
          queryClient.invalidateQueries({ queryKey: inviteListInvitesQueryKey() });
          invalidateAdminApprovals(queryClient);
        }}
        open={isInviteModalOpen}
        roles={rolesList}
      />
      <PageActions>
        <ReviewChangesButton onClick={startReview} />
        <Tooltip
          content={canWriteUsers ? INVITE_REQUIRES_MFA_TOOLTIP : MANAGE_USERS_PERMISSION_TOOLTIP}
          isOpen={canWriteUsers && (hasMfaEnabled || !currentUser) ? false : undefined}
          placement="left"
        >
          <Button
            onClick={openInviteModal}
            icon={<Icon.Mail />}
            size="small"
            isDisabled={!canWriteUsers || !hasMfaEnabled}
          >
            Invite User
          </Button>
        </Tooltip>
      </PageActions>
      <Loading isLoading={isUsersLoading || isInvitesLoading}>
        <SectionHeader text="Users" />
        <Inner pt={0} px="var(--page-margin)">
          <DataTable<UserRow>
            {...tableDefaults}
            columns={userColumns}
            data={userList}
            isLoading={isUsersFetching}
            rowKey={(row) => row.id}
            rowAction={userRowAction}
            rowClassName={(row) =>
              pendingRoleAssignByUser.has(row.id) ? 'group/row min-h-11' : 'min-h-11 cursor-default'
            }
            onRowClick={(row) => {
              const pendingAssign = pendingRoleAssignByUser.get(row.id);
              if (pendingAssign) openReview(pendingAssign);
            }}
            emptyContent={<TableEmpty icon={<Icon.AnimationUser />} title="No team members found" />}
          />
        </Inner>
        <SectionHeader text="Pending Invites" border />
        <Inner pt={0} px="var(--page-margin)">
          <DataTable<InviteRow>
            {...tableDefaults}
            columns={inviteColumns}
            data={inviteList}
            isLoading={isInvitesFetching}
            rowKey={(row) => row.id}
            rowAction={inviteRowAction}
            rowClassName={(row) => (row.approval ? 'group/row min-h-11' : 'min-h-11 cursor-default')}
            onRowClick={(row) => row.approval && openReview(row.approval)}
            emptyMessage="No pending invites"
          />
        </Inner>
      </Loading>
    </>
  );
};
